import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useState, memo, useEffect, useMemo, useRef } from 'react';

import ClearSearchButton from '../../../components/ClearSearchButton';
import type { RBACUser } from '../../../types';
import { sortByDisplayName } from '../sortByDisplayName';

type Props = {
  items: RBACUser[];
  selectedId?: string;
  onSelect: (value: RBACUser) => void;
  // For "add" boxes (no persistent selection): clear the input after a pick so the
  // next one can be queued. Defaults off, preserving the select-and-fill behaviour.
  clearOnSelect?: boolean;
  placeholder?: string;
  // Invoked by the in-input X button after the text is cleared — lets the caller
  // also drop the current selection (e.g. navigate back to the unselected route).
  onClear?: () => void;
};

function Autocomplete({ items, selectedId, onSelect, clearOnSelect, placeholder, onClear }: Props) {
  const [value, setValue] = useState('');

  // userList is the filtered list of users, initially it is the same as items
  const [userList, setUserList] = useState<RBACUser[]>(items);

  // Fill the input with the selected user's name when the selected user changes
  useEffect(() => {
    const selectedUser = items.find((user) => user.id === selectedId);
    if (selectedUser) {
      setValue(selectedUser.displayName!);
    }
  }, [selectedId, items]);

  // Filter userList based on entered value
  useEffect(() => {
    setUserList(
      items.filter(
        (item) =>
          item.displayName?.toLowerCase().includes(value.toLowerCase()) ||
          item.userPrincipalName?.toLowerCase().includes(value.toLowerCase())
      )
    );
  }, [value, items]);

  // Memoized so downstream render only re-sorts when the filtered list actually changes.
  const sortedList = useMemo(() => sortByDisplayName(userList), [userList]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // The daisyUI .dropdown reveals its content purely via CSS :focus-within, so we can't
  // both keep the input focused after a pick AND have the list stay closed unless we gate
  // whether the content is rendered at all. isOpen is that gate: it starts true (the list
  // is rendered but stays CSS-hidden until focus, as before) and is only force-closed after
  // a pick, staying closed until the next focus/keystroke.
  const [isOpen, setIsOpen] = useState(true);
  // Picking refocuses the input to keep it ready for the next entry; that refocus fires
  // onFocus, which would otherwise re-open the list we just closed. This one-shot flag
  // swallows exactly that programmatic refocus.
  const suppressOpenRef = useRef(false);

  // Close the list but keep the input focused so the next name can be typed straight away.
  // The suppress flag stops the refocus below from immediately re-opening the list.
  const closeAndRefocus = () => {
    suppressOpenRef.current = true;
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleClick = (item: RBACUser) => {
    onSelect(item);
    if (clearOnSelect) setValue('');
    closeAndRefocus();
  };

  // Select all text when input is focused, and open the list — unless this focus came from
  // the programmatic refocus after a pick.
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false;
      return;
    }
    e.target.select();
    setIsOpen(true);
  };

  // Arrow down from the input jumps into the first result; Escape closes the list.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      listRef.current?.querySelector<HTMLElement>('li')?.focus();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Arrows move between options; arrow up from the first returns to the input
  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, item: RBACUser) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(item);
    } else if (e.key === 'Escape') {
      closeAndRefocus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      (e.currentTarget.nextElementSibling as HTMLElement | null)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const previous = e.currentTarget.previousElementSibling as HTMLElement | null;
      if (previous) {
        previous.focus();
      } else {
        inputRef.current?.focus();
      }
    }
  };

  return (
    <div className="dropdown w-72">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="input w-full pr-9 focus:outline-none"
          placeholder={placeholder ?? 'Search for a user'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsOpen(true);
          }}
          tabIndex={0}
          onFocus={handleFocus}
          onKeyDown={handleInputKeyDown}
        />
        {value && (
          <ClearSearchButton
            onClick={() => {
              setValue('');
              onClear?.();
              inputRef.current?.focus();
            }}
          />
        )}
      </div>
      {!isOpen || userList.length === 0 ? (
        <div></div>
      ) : (
        <div className="w-full dropdown-content border border-base-200 bg-base-100 top-14 max-h-[50rem] overflow-auto flex-col rounded-md">
          <ul className="menu menu-sm w-full" role="listbox" ref={listRef}>
            {sortedList.map((item) => {
              return (
                <li
                  key={item.id}
                  // 0, not index+1: positive tabindex sorts before the search
                  // input globally, making the list unreachable by Tab
                  tabIndex={0}
                  role="option"
                  aria-selected={item.id === selectedId}
                  onClick={() => {
                    handleClick(item);
                  }}
                  onKeyDown={(e) => handleOptionKeyDown(e, item)}
                  className="group border-b last:border-b-0 border-b-base-200 w-full focus:outline-none"
                  title={!item.accountEnabled ? 'User is disabled' : ''}
                >
                  {/* Hover/keyboard-focus paints the option primary (purple in the
                      dark theme), matching the active nav button and GroupAutoComplete2. */}
                  <div className="flex flex-col items-start gap-0 w-full group-hover:bg-primary group-hover:text-primary-content group-focus:bg-primary group-focus:text-primary-content">
                    <div className="flex w-full flex-row items-center justify-between gap-1">
                      <div className="min-w-0 truncate">{item.displayName}</div>
                      {!item.accountEnabled && (
                        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-warning" />
                      )}
                    </div>
                    <div className="text-xs w-full truncate" title={item.userPrincipalName}>
                      {item.userPrincipalName}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default memo(Autocomplete);
